// Insight Console — หน้าของ dev/Support: สวิตช์ระบบ, นิยามตัวเลข, จับคู่แผนก, เป้าขาย, ปฏิทิน, ดู KPI แทนผู้ใช้, insight รายวัน
// ทุก endpoint ฝั่ง server ตรวจสิทธิ์ persona "support" ซ้ำ
import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, MenuItem, Paper, Select, Switch, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography, Checkbox, FormControlLabel,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Swal from 'sweetalert2';
import api from '../adapters/api';
import { KpiCards } from '../components/insights/KpiCards';
import InsightCard, { downloadAttachment } from '../components/insights/InsightCard';
import InsightRowsDrawer from '../components/insights/InsightRowsDrawer';
import { startViewAs, stopViewAs, personaText } from '../components/insights/ViewAsBanner';
import { useInsightMe } from '../components/insights/useInsightInline';
import { useNavigate } from 'react-router-dom';
import { money } from '../components/insights/format';

const DEPT_LABELS = {
  company: 'รวม (บริษัท)', credit: 'เซลล์เครดิต', online: 'เซลล์ออนไลน์',
  store_st: 'เซลล์หน้าร้าน-สันทราย', store_sk: 'เซลล์หน้าร้าน-สันกำแพง',
};
const DEPT_ORDER = ['company', 'credit', 'online', 'store_st', 'store_sk'];
const THAI_MONTHS = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'];

const showError = (err) => Swal.fire({
  icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.error || err.message || '',
  confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
});
const showSaved = (title = 'บันทึกแล้ว') => Swal.fire({ icon: 'success', title, timer: 1200, showConfirmButton: false });

const thaiDate = (iso) => {
  const [y, m, d] = iso.split('-').map(Number);
  return `${d} ${THAI_MONTHS[m - 1]} ${y + 543}`;
};

function Section({ title, children, action }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      {children}
    </Paper>
  );
}

function UploadButton({ label, onFile }) {
  return (
    <Button variant="outlined" size="small" component="label" startIcon={<UploadFileIcon />}>
      {label}
      <input hidden type="file" accept=".xlsx,.csv" onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) onFile(f); }} />
    </Button>
  );
}

// เวลาเริ่มให้ผู้ใช้เห็น: เปิดสวิตช์ไว้ล่วงหน้าได้ ผู้ใช้ทั่วไปเริ่มเห็นตั้งแต่เวลานี้ (dev/Support ดูแทนผู้ใช้ได้เสมอ)
const toLocalInput = iso => {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const thaiDateTime = iso => new Date(iso).toLocaleString('th-TH', { dateStyle: 'long', timeStyle: 'short' });

function GoLiveSection({ goLive, refresh }) {
  const [value, setValue] = useState(() => toLocalInput(goLive?.at));
  const save = async (at) => {
    try {
      await api.put('/api/insight-console/go-live', { at });
      refresh();
      Swal.fire({ icon: 'success', title: at ? 'ตั้งเวลาเริ่มใช้งานแล้ว' : 'ล้างเวลาเริ่มใช้งานแล้ว', timer: 1300, showConfirmButton: false });
    } catch (err) { showError(err); }
  };
  return (
    <Section title="เริ่มให้ผู้ใช้เห็นตั้งแต่">
      {goLive?.at
        ? (
          <Alert severity={goLive.live ? 'success' : 'info'} sx={{ mb: 1.5 }}>
            {goLive.live
              ? <>เริ่มใช้งานแล้วตั้งแต่ <b>{thaiDateTime(goLive.at)}</b> — ผู้ใช้เห็นตามสวิตช์ด้านล่าง</>
              : <>ผู้ใช้จะเริ่มเห็นตั้งแต่ <b>{thaiDateTime(goLive.at)}</b> — ก่อนเวลานี้ผู้ใช้ยังไม่เห็นอะไร แม้สวิตช์จะเปิดอยู่ (ดูแทนผู้ใช้ได้ตามปกติ)</>}
          </Alert>
        )
        : (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            ไม่ได้ตั้งเวลา — ผู้ใช้เห็นทันทีที่สวิตช์เปิด · ตั้งเวลาไว้เพื่อเปิดสวิตช์ล่วงหน้าแล้วให้เริ่มพร้อมกันตามเวลา
          </Typography>
        )}
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <TextField
          type="datetime-local" size="small" label="วันเวลาเริ่ม" value={value}
          onChange={e => setValue(e.target.value)} InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" disabled={!value} onClick={() => save(new Date(value).toISOString())}>บันทึกเวลา</Button>
        {goLive?.at && <Button color="inherit" onClick={() => { setValue(''); save(null); }}>ล้างเวลา</Button>}
      </Box>
    </Section>
  );
}

// ── แท็บ: สถานะ & สวิตช์ ───────────────────────────────────────────────
function StatusTab({ status, refresh }) {
  const toggle = async (flag) => {
    const turningOn = !flag.enabled;
    if (flag.key === 'insight.enabled' && turningOn) {
      const r = await Swal.fire({
        icon: 'question', title: 'เปิดระบบ insight?',
        text: status.goLive?.at && !status.goLive.live
          ? `ผู้ใช้ที่เปิด persona ไว้จะเริ่มเห็นข้อมูลตั้งแต่ ${thaiDateTime(status.goLive.at)}`
          : 'ผู้ใช้ที่เปิด persona ไว้จะเริ่มเห็นข้อมูลทันที',
        showCancelButton: true, confirmButtonText: 'เปิดระบบ', cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#2e7d32', cancelButtonColor: '#757575',
      });
      if (!r.isConfirmed) return;
    }
    try {
      await api.patch(`/api/insight-console/flags/${encodeURIComponent(flag.key)}`, { enabled: turningOn });
      refresh();
    } catch (err) { showError(err); }
  };

  return (
    <>
      {status.dbMode !== 'restricted' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          ระบบ insight ใช้ DB user หลักของแอปอยู่ (โหมด {status.dbMode}) — แนะนำให้สร้าง role <b>ccs_insight_app</b> ตาม backend/sql/insight/000a_setup_cmsteel.sql
        </Alert>
      )}
      {status.calendar.overrides === 0 && <Alert severity="info" sx={{ mb: 2 }}>ยังไม่ได้นำเข้าวันหยุดของปี {status.calendar.year + 543}</Alert>}
      {status.calendar.nextYearMissing && <Alert severity="info" sx={{ mb: 2 }}>เดือนธันวาคมแล้ว — อย่าลืมนำเข้าวันหยุดของปีถัดไป</Alert>}
      <GoLiveSection key={status.goLive?.at || 'none'} goLive={status.goLive} refresh={refresh} />
      <Section title="สวิตช์ระบบ">
        <Table size="small">
          <TableBody>
            {status.flags.map(f => (
              <TableRow key={f.key}>
                <TableCell sx={{ width: 70 }}><Switch checked={f.enabled} onChange={() => toggle(f)} /></TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>{f.description || f.key}</Typography>
                  <Typography variant="caption" color="text.secondary">{f.key}{f.updated_by ? ` · แก้ล่าสุดโดย ${f.updated_by}` : ''}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Section>
      <Section title="ข้อมูลระบบ">
        <Typography variant="body2">โหมดฐานข้อมูล: <b>{status.dbMode}</b> · migration: {status.migrations.map(m => m.version).join(', ') || '-'}</Typography>
        <Typography variant="body2">นิยามยอดขาย: เวอร์ชัน {status.definition.version}</Typography>
        <Typography variant="body2">เป้าขายที่มี: {status.targets.map(t => `${t.year + 543} (${t.months} เดือน)`).join(', ') || 'ยังไม่มี'}</Typography>
      </Section>
    </>
  );
}

// ── แท็บ: นิยามตัวเลข & จับคู่แผนก ─────────────────────────────────────
function DefinitionTab() {
  const qc = useQueryClient();
  const { data: def } = useQuery({ queryKey: ['ic-definition'], queryFn: async () => (await api.get('/api/insight-console/definition')).data });
  const { data: map, refetch } = useQuery({ queryKey: ['ic-sales-map'], queryFn: async () => (await api.get('/api/insight-console/sales-map')).data });
  const [form, setForm] = useState(null);
  const [onlyUnmapped, setOnlyUnmapped] = useState(true);
  const f = form || (def && { sale_flag: def.sale_flag, freight_item_code: def.freight_item_code || '', deduct: (def.deduct_credit_note_flags || []).join(',') });

  const save = async () => {
    try {
      await api.put('/api/insight-console/definition', {
        sale_flag: f.sale_flag, freight_item_code: f.freight_item_code,
        deduct_credit_note_flags: String(f.deduct).split(',').map(s => s.trim()).filter(Boolean),
      });
      setForm(null);
      qc.invalidateQueries({ queryKey: ['ic-definition'] });
      showSaved('บันทึกนิยามแล้ว');
    } catch (err) { showError(err); }
  };

  const setDept = async (saleCode, dept) => {
    try {
      await api.put(`/api/insight-console/sales-map/${encodeURIComponent(saleCode)}`, { dept_key: dept || null });
      refetch();
    } catch (err) { showError(err); }
  };

  const rows = (map?.list || []).filter(r => !onlyUnmapped || !r.dept);

  return (
    <>
      <Section title={`นิยาม "ยอดขาย" (เวอร์ชัน ${def?.version ?? '-'})`} action={<Button size="small" variant="contained" onClick={save} disabled={!form}>บันทึก</Button>}>
        {f && (
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <TextField size="small" label="trans_flag ของบิลขาย" value={f.sale_flag} onChange={e => setForm({ ...f, sale_flag: e.target.value })} />
            <TextField size="small" label="รหัสสินค้าค่าส่ง (หักออก)" value={f.freight_item_code} onChange={e => setForm({ ...f, freight_item_code: e.target.value })} />
            <TextField size="small" label="trans_flag ใบลดหนี้ที่หักออก (คั่นด้วย ,)" value={f.deduct} onChange={e => setForm({ ...f, deduct: e.target.value })} sx={{ minWidth: 280 }} />
          </Box>
        )}
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          ยอดขาย = ยอดก่อน VAT ของบิลขาย − บรรทัดค่าส่ง − ใบลดหนี้/รับคืน · ทุกการแก้จะเพิ่มเวอร์ชันและเก็บประวัติ
        </Typography>
      </Section>
      <Section
        title="จับคู่รหัสเซลล์ → แผนก"
        action={<FormControlLabel control={<Checkbox size="small" checked={onlyUnmapped} onChange={e => setOnlyUnmapped(e.target.checked)} />} label="เฉพาะที่ยังไม่มีแผนก" />}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          แผนกมาจากกลุ่มใน SML อัตโนมัติ (ออนไลน์มาก่อนเครดิต) · รหัสที่ไม่มีกลุ่มนับเฉพาะยอดรวมบริษัทจนกว่าจะจับคู่ที่นี่
        </Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>รหัส</TableCell><TableCell>ชื่อ</TableCell><TableCell align="right">บิล 90 วัน</TableCell>
              <TableCell align="right">ยอด 90 วัน</TableCell><TableCell>แผนก</TableCell><TableCell>ที่มา</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.saleCode}>
                <TableCell>{r.saleCode}</TableCell>
                <TableCell>{r.name || <Chip size="small" label="ไม่อยู่ใน erp_user" />}</TableCell>
                <TableCell align="right">{r.bills90d.toLocaleString('th-TH')}</TableCell>
                <TableCell align="right">{money(r.amount90d)}</TableCell>
                <TableCell>
                  <Select size="small" value={r.dept || ''} displayEmpty onChange={e => setDept(r.saleCode, e.target.value)} sx={{ minWidth: 190 }}>
                    <MenuItem value=""><em>ไม่มีแผนก</em></MenuItem>
                    {Object.entries(map.depts).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
                  </Select>
                </TableCell>
                <TableCell>{r.source === 'override' ? 'กำหนดเอง' : r.source === 'group' ? 'กลุ่ม SML' : '-'}</TableCell>
              </TableRow>
            ))}
            {!rows.length && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">ไม่มีรายการ</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>
    </>
  );
}

// ── แท็บ: เป้าขาย ───────────────────────────────────────────────────────
function TargetsTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const { data: rows = [], refetch } = useQuery({
    queryKey: ['ic-targets', year],
    queryFn: async () => (await api.get('/api/insight-console/targets', { params: { year } })).data,
  });

  const importFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data: pv } = await api.post('/api/insight-console/targets/import', fd);
      if (pv.errors.length) {
        return Swal.fire({
          icon: 'error', title: 'ไฟล์มีแถวที่ไม่ถูกต้อง',
          html: pv.errors.slice(0, 10).map(e => `แถว ${e.line}: ${e.message}`).join('<br>'),
          confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง',
        });
      }
      const r = await Swal.fire({
        icon: pv.overwrites ? 'warning' : 'question',
        title: 'นำเข้าเป้าขาย?',
        text: `${pv.valid.length} แถว${pv.overwrites ? ` · ทับเป้าเดิม ${pv.overwrites} รายการ` : ''}`,
        showCancelButton: true, confirmButtonText: 'นำเข้าข้อมูล', cancelButtonText: 'ยกเลิก',
        confirmButtonColor: pv.overwrites ? '#d32f2f' : '#1976d2', cancelButtonColor: '#757575',
      });
      if (!r.isConfirmed) return;
      await api.post('/api/insight-console/targets/import?commit=1', fd);
      refetch();
      showSaved('นำเข้าเป้าขายแล้ว');
    } catch (err) { showError(err); }
  };

  const cell = (m, k) => rows.find(r => r.month === m && r.dept_key === k)?.target_amount;

  return (
    <Section
      title="เป้าขายรายเดือน"
      action={
        <>
          <Select size="small" value={year} onChange={e => setYear(e.target.value)}>
            {[year - 1, year, year + 1].map(y => <MenuItem key={y} value={y}>{y + 543}</MenuItem>)}
          </Select>
          <UploadButton label="นำเข้าไฟล์" onFile={importFile} />
        </>
      }
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        รูปแบบไฟล์: คอลัมน์ ปี | เดือน | เป้า | แผนก (รวม, เซลล์เครดิต, เซลล์ออนไลน์, เซลล์หน้าร้าน-สันทราย, เซลล์หน้าร้าน-สันกำแพง)
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>เดือน</TableCell>
              {DEPT_ORDER.map(k => <TableCell key={k} align="right">{DEPT_LABELS[k]}</TableCell>)}
            </TableRow>
          </TableHead>
          <TableBody>
            {THAI_MONTHS.map((label, i) => (
              <TableRow key={label}>
                <TableCell>{label}</TableCell>
                {DEPT_ORDER.map(k => <TableCell key={k} align="right">{cell(i + 1, k) != null ? money(cell(i + 1, k)) : '-'}</TableCell>)}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
    </Section>
  );
}

// ── แท็บ: ปฏิทิน ────────────────────────────────────────────────────────
function CalendarTab() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [newDate, setNewDate] = useState('');
  const { data: rows = [], refetch } = useQuery({
    queryKey: ['ic-calendar', year],
    queryFn: async () => (await api.get('/api/insight-console/calendar', { params: { year } })).data,
  });

  const importFile = async (file) => {
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data: pv } = await api.post('/api/insight-console/calendar/import', fd);
      const warnHtml = pv.warnings.length
        ? `<div style="text-align:left;margin-top:8px"><b>ควรตรวจ:</b><br>${pv.warnings.map(w => `${thaiDate(w.date)} — ${w.message}`).join('<br>')}<br>(วันเหล่านี้จะไม่ถูกนำเข้า — เพิ่มเองภายหลังได้ถ้าเป็นวันหยุดจริง)</div>`
        : '';
      const errHtml = pv.errors.length ? `<div style="text-align:left;margin-top:8px"><b>แถวที่อ่านไม่ได้:</b><br>${pv.errors.slice(0, 10).map(e => `แถว ${e.line}: ${e.message}`).join('<br>')}</div>` : '';
      const r = await Swal.fire({
        icon: pv.warnings.length || pv.errors.length ? 'warning' : 'question',
        title: `นำเข้าวันหยุด ${pv.valid.length - pv.warnings.length} วัน?`,
        html: warnHtml + errHtml,
        showCancelButton: true, confirmButtonText: 'นำเข้าข้อมูล', cancelButtonText: 'ยกเลิก',
        confirmButtonColor: '#1976d2', cancelButtonColor: '#757575',
      });
      if (!r.isConfirmed) return;
      pv.warnings.forEach(w => fd.append('skip_dates', w.date));
      await api.post('/api/insight-console/calendar/import?commit=1', fd);
      refetch();
      showSaved('นำเข้าวันหยุดแล้ว');
    } catch (err) { showError(err); }
  };

  const addHoliday = async () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(newDate)) return;
    try {
      await api.put(`/api/insight-console/calendar/${newDate}`, { is_workday: false, note: '' });
      setNewDate('');
      refetch();
    } catch (err) { showError(err); }
  };

  const remove = async (date) => {
    const r = await Swal.fire({
      icon: 'warning', title: 'ลบวันนี้ออกจากปฏิทิน?', text: `${thaiDate(date)} จะกลับเป็นค่าเริ่มต้น (จ.–ส. ทำงาน, อา. หยุด)`,
      showCancelButton: true, confirmButtonText: 'ลบ', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#d32f2f', cancelButtonColor: '#757575',
    });
    if (!r.isConfirmed) return;
    try { await api.delete(`/api/insight-console/calendar/${date}`); refetch(); } catch (err) { showError(err); }
  };

  return (
    <Section
      title="ปฏิทินวันทำงาน"
      action={
        <>
          <Select size="small" value={year} onChange={e => setYear(e.target.value)}>
            {[year - 1, year, year + 1].map(y => <MenuItem key={y} value={y}>{y + 543}</MenuItem>)}
          </Select>
          <UploadButton label="นำเข้าไฟล์" onFile={importFile} />
        </>
      }
    >
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        ค่าเริ่มต้น: จันทร์–เสาร์ทำงาน อาทิตย์หยุด · รายการด้านล่างคือวันที่ต่างจากค่าเริ่มต้น · ไฟล์: คอลัมน์ "วันหยุด" (วัน/เดือน/ปี ค.ศ.)
      </Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
        <TextField size="small" type="date" value={newDate} onChange={e => setNewDate(e.target.value)} />
        <Button size="small" variant="outlined" onClick={addHoliday} disabled={!newDate}>เพิ่มวันหยุด</Button>
      </Box>
      <Table size="small">
        <TableBody>
          {rows.map(r => (
            <TableRow key={r.date}>
              <TableCell>{thaiDate(r.date)}</TableCell>
              <TableCell>{r.is_workday ? <Chip size="small" color="success" label="วันทำงาน" /> : <Chip size="small" label="วันหยุด" />}</TableCell>
              <TableCell>{r.note}</TableCell>
              <TableCell align="right"><Button size="small" color="error" onClick={() => remove(r.date)}>ลบ</Button></TableCell>
            </TableRow>
          ))}
          {!rows.length && <TableRow><TableCell><Typography variant="body2" color="text.secondary">ยังไม่มีข้อมูลของปีนี้</Typography></TableCell></TableRow>}
        </TableBody>
      </Table>
    </Section>
  );
}

// ── แท็บ: ดู KPI แทนผู้ใช้ ──────────────────────────────────────────────
const PERSONA_LABELS = {
  sales_member: 'เซลล์', sales_head: 'หัวหน้าขาย', salesadmin_member: 'ธุรการ', salesadmin_head: 'หัวหน้าธุรการ',
};

function PreviewTab() {
  const [user, setUser] = useState(null);
  const { data: users = [] } = useQuery({ queryKey: ['ic-users'], queryFn: async () => (await api.get('/api/insight-console/users')).data });
  const { data, isFetching, error } = useQuery({
    queryKey: ['ic-preview', user?.user_code],
    queryFn: async () => (await api.get('/api/insight-console/kpi-preview', { params: { user_code: user.user_code } })).data,
    enabled: Boolean(user),
  });

  return (
    <Section title="ดู KPI แบบเดียวกับที่ผู้ใช้เห็น">
      <Autocomplete
        size="small" sx={{ maxWidth: 420, mb: 2 }}
        options={users} value={user} onChange={(_, v) => setUser(v)}
        getOptionLabel={u => `${u.user_name} (${u.user_code})`}
        isOptionEqualToValue={(a, b) => a.user_code === b.user_code}
        renderInput={p => <TextField {...p} label="เลือกผู้ใช้" />}
      />
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        การเปิดดูทุกครั้งถูกบันทึกไว้ (audit) · แสดงแม้สวิตช์ของ persona ยังปิดอยู่
      </Typography>
      {isFetching && <CircularProgress size={20} />}
      {error && <Alert severity="error">{error.response?.data?.error || error.message}</Alert>}
      {data && !isFetching && (
        <>
          <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
            {data.personas.length
              ? data.personas.map(p => <Chip key={p.persona} size="small" color="primary" label={PERSONA_LABELS[p.persona] || p.persona} />)
              : <Alert severity="info" sx={{ py: 0 }}>ผู้ใช้นี้ไม่มี persona ในช่วงนำร่อง</Alert>}
          </Box>
          {data.kpi.sections.length > 0 && <KpiCards kpi={data.kpi} />}
        </>
      )}
    </Section>
  );
}

const ANGLE_LABELS = {
  sales_week_buyers: 'ลูกค้าที่คาดว่าจะซื้อ', sales_customer_drop: 'ลูกค้ายอดลดลง', sales_open_quotes: 'QT ยังไม่แปลง',
  head_people_pace: 'เซลล์ยอดต่างจากปกติ', head_dept_target: 'แผนกคาดไม่ถึงเป้า',
  admin_so_ready: 'SO พร้อมออกบิล', admin_items_short: 'สินค้าไม่พอสำหรับ SO',
};
const JOB_STATUS = { done: ['success', 'สำเร็จ'], running: ['info', 'กำลังรัน'], failed: ['error', 'ล้มเหลว'] };
const DAY_STATUS = {
  ready: 'พร้อมแล้ว', running: 'กำลังสร้าง', pending: 'ยังไม่ถึง 06:00', not_ready: 'ยังไม่พร้อม (ยังไม่ได้รัน/ล้มเหลว)', holiday: 'วันหยุด',
};
const todayIso = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);
const timeTh = iso => (iso ? new Date(iso).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Bangkok' }) : '');

// ตัวอย่างรายวัน: ผู้ใช้แต่ละคนได้ insight อะไร (โหมดอ่านอย่างเดียว) + candidate ที่ไม่ถูกเลือก + สถานะ job
function DailyTab({ status, refresh }) {
  const qc = useQueryClient();
  const [date, setDate] = useState(todayIso());
  const [userCode, setUserCode] = useState(null);
  const [showCandidates, setShowCandidates] = useState(false);
  const [rowsTask, setRowsTask] = useState(null);
  const [running, setRunning] = useState(false);

  const { data: jobs = [] } = useQuery({ queryKey: ['ic-jobs'], queryFn: async () => (await api.get('/api/insight-console/jobs')).data });
  const { data: day, isFetching } = useQuery({
    queryKey: ['ic-daily', date],
    queryFn: async () => (await api.get('/api/insight-console/daily', { params: { date } })).data,
  });
  const { data: detail } = useQuery({
    queryKey: ['ic-daily-user', date, userCode],
    queryFn: async () => (await api.get('/api/insight-console/daily', { params: { date, user_code: userCode } })).data,
    enabled: Boolean(userCode),
  });
  const generateOn = status.flags.find(f => f.key === 'job.daily_generate')?.enabled;

  const runNow = async () => {
    const ok = await Swal.fire({
      icon: 'question', title: 'สร้าง insight ของวันนี้ตอนนี้?',
      text: 'สร้างใหม่ทับชุดของวันนี้ (feedback/สถานะที่ผู้ใช้กดไว้ยังอยู่) — ผู้ใช้จะเห็นเฉพาะ persona/ช่องทางที่เปิดสวิตช์',
      showCancelButton: true, confirmButtonText: 'สร้างตอนนี้', cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#1976d2', cancelButtonColor: '#757575',
    });
    if (!ok.isConfirmed) return;
    setRunning(true);
    try {
      const { data } = await api.post('/api/insight-console/run-now');
      setDate(data.today);
      qc.invalidateQueries({ queryKey: ['ic-jobs'] });
      qc.invalidateQueries({ queryKey: ['ic-daily'] });
      qc.invalidateQueries({ queryKey: ['ic-daily-user'] });
      Swal.fire({
        icon: data.errors?.length ? 'warning' : 'success',
        title: `สร้างแล้ว ${data.selected} เรื่อง ให้ ${data.users} คน`,
        text: data.errors?.length ? `มีข้อผิดพลาด ${data.errors.length} รายการ (ดูในตารางการทำงานของระบบ)` : `ใช้เวลา ${(data.ms / 1000).toFixed(1)} วินาที`,
        ...(data.errors?.length ? { confirmButtonColor: '#1976d2', confirmButtonText: 'ตกลง' } : { timer: 1500, showConfirmButton: false }),
      });
    } catch (err) {
      showError(err);
    } finally {
      setRunning(false);
    }
  };

  const tasksShown = (detail?.tasks || []).filter(t => showCandidates || t.selected);

  return (
    <>
      <Section
        title="การทำงานของระบบ"
        action={<Button variant="contained" size="small" disabled={running} onClick={runNow}>{running ? 'กำลังสร้าง…' : 'สร้าง insight ของวันนี้ตอนนี้'}</Button>}
      >
        {!generateOn && (
          <Alert severity="info" sx={{ mb: 1 }}>
            สวิตช์ job.daily_generate ปิดอยู่ — ระบบยังไม่สร้างอัตโนมัติ 06:00 (กดสร้างเองได้) ·{' '}
            <Button size="small" onClick={async () => {
              try { await api.patch('/api/insight-console/flags/job.daily_generate', { enabled: true }); refresh(); showSaved('เปิดการสร้างอัตโนมัติแล้ว'); }
              catch (err) { showError(err); }
            }}>เปิดสร้างอัตโนมัติ</Button>
          </Alert>
        )}
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>วันที่</TableCell><TableCell>สถานะ</TableCell><TableCell>เวลา</TableCell><TableCell align="right">ผู้ใช้</TableCell><TableCell align="right">เลือกแสดง / ทั้งหมด</TableCell><TableCell>ข้อผิดพลาด</TableCell></TableRow>
          </TableHead>
          <TableBody>
            {jobs.slice(0, 7).map(j => (
              <TableRow key={j.id}>
                <TableCell>{thaiDate(j.run_date)}</TableCell>
                <TableCell><Chip size="small" color={JOB_STATUS[j.status]?.[0] || 'default'} label={JOB_STATUS[j.status]?.[1] || j.status} /></TableCell>
                <TableCell>{timeTh(j.started_at)}{j.summary?.ms != null ? ` (${(j.summary.ms / 1000).toFixed(1)} วิ)` : ''}</TableCell>
                <TableCell align="right">{j.summary?.users ?? '-'}</TableCell>
                <TableCell align="right">{j.summary ? `${j.summary.selected} / ${j.summary.candidates}` : '-'}</TableCell>
                <TableCell sx={{ maxWidth: 360 }}>
                  {j.error && <Typography variant="caption" color="error" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{j.error.split('\n').slice(0, 3).join('\n')}</Typography>}
                </TableCell>
              </TableRow>
            ))}
            {!jobs.length && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="text.secondary">ยังไม่เคยรัน</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
      </Section>

      <Section title="ตัวอย่างรายวัน (ชุด insight ที่ผู้ใช้เห็นจริง)">
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
          <TextField type="date" size="small" label="วันที่" value={date} onChange={e => { setDate(e.target.value); setUserCode(null); }} InputLabelProps={{ shrink: true }} />
          {day && <Chip size="small" label={DAY_STATUS[day.status] || day.status} />}
          {isFetching && <CircularProgress size={18} />}
        </Box>
        {day?.angles?.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
            {day.angles.map(a => <Chip key={a.angle_code} size="small" variant="outlined" label={`${ANGLE_LABELS[a.angle_code] || a.angle_code}: ${a.selected}/${a.total}`} />)}
          </Box>
        )}
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ผู้ใช้</TableCell><TableCell>persona</TableCell><TableCell align="right">แสดง</TableCell><TableCell align="right">ไม่ถูกเลือก</TableCell>
              <TableCell align="right">เห็นแล้ว</TableCell><TableCell align="right">👍</TableCell><TableCell align="right">👎</TableCell><TableCell align="right">ทำแล้ว</TableCell><TableCell align="right">ข้าม</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(day?.users || []).map(u => (
              <TableRow key={u.user_code} hover selected={u.user_code === userCode} sx={{ cursor: 'pointer' }} onClick={() => setUserCode(u.user_code)}>
                <TableCell>{u.user_code}</TableCell>
                <TableCell>{u.personas.split(',').map(p => PERSONA_LABELS[p] || p).join(', ')}</TableCell>
                <TableCell align="right">{u.selected}</TableCell><TableCell align="right">{u.candidates}</TableCell>
                <TableCell align="right">{u.seen}</TableCell><TableCell align="right">{u.up}</TableCell><TableCell align="right">{u.down}</TableCell>
                <TableCell align="right">{u.done}</TableCell><TableCell align="right">{u.skipped}</TableCell>
              </TableRow>
            ))}
            {day && !day.users.length && <TableRow><TableCell colSpan={9}><Typography variant="body2" color="text.secondary">ไม่มี insight ของวันนี้</Typography></TableCell></TableRow>}
          </TableBody>
        </Table>
        {userCode && detail && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ flex: 1 }}>{userCode}: เห็น {detail.tasks.filter(t => t.selected).length} เรื่อง</Typography>
              <FormControlLabel control={<Checkbox size="small" checked={showCandidates} onChange={e => setShowCandidates(e.target.checked)} />} label="แสดงเรื่องที่ไม่ถูกเลือก" />
            </Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>การเปิดดูถูกบันทึกไว้ (audit)</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {tasksShown.map(t => (
                <InsightCard
                  key={t.id} task={t}
                  actions={t.attachment ? {
                    openRows: () => setRowsTask(t),
                    download: () => downloadAttachment(`/api/insight-console/tasks/${t.id}/attachment`),
                  } : undefined}
                  extra={(
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      <Chip size="small" color={t.selected ? 'primary' : 'default'} label={t.selected ? `อันดับ ${t.rank}` : t.internal.repeat ? 'ไม่เลือก: ซ้ำ ไม่มีรายการใหม่' : 'ไม่เลือก: เกินโควต้า'} />
                      <Chip size="small" variant="outlined" label={ANGLE_LABELS[t.angle] || t.angle} />
                      <Chip size="small" variant="outlined" label={`คะแนน ${Number(t.score).toFixed(2)} · ใหม่ ${t.internal.newItems ?? 0}`} />
                      {t.events.map(e => <Chip key={e} size="small" variant="outlined" color="success" label={e} />)}
                      {t.status !== 'open' && <Chip size="small" label={t.status === 'done' ? 'ทำแล้ว' : 'ข้าม'} />}
                    </Box>
                  )}
                />
              ))}
            </Box>
          </Box>
        )}
      </Section>
      <InsightRowsDrawer
        open={Boolean(rowsTask)} onClose={() => setRowsTask(null)}
        url={rowsTask ? `/api/insight-console/tasks/${rowsTask.id}/rows` : null}
        title={rowsTask?.title}
        onDownload={rowsTask ? () => downloadAttachment(`/api/insight-console/tasks/${rowsTask.id}/attachment`) : undefined}
      />
    </>
  );
}

// เข้าโหมด "ดูในฐานะผู้ใช้" — ทั้งหน้าเว็บแสดง insight ของผู้ใช้นั้น (ไม่ต้องเปิดสวิตช์/แก้ .env) อ่านอย่างเดียว
function ViewAsPicker() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data: users = [] } = useQuery({ queryKey: ['ic-users'], queryFn: async () => (await api.get('/api/insight-console/users')).data });
  const { data: me } = useInsightMe();

  const go = async () => {
    setBusy(true);
    const ok = await startViewAs(qc, user.user_code);
    setBusy(false);
    if (ok) navigate('/');
  };

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderRadius: 2 }}>
      <Typography variant="subtitle2" fontWeight={700}>ทดลองหน้าเว็บในฐานะผู้ใช้</Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        เห็นแถบหน้าแรก, 💡, ป๊อปอัป, ป้ายในหน้าจอ และหน้า insight ของฉัน เหมือนผู้ใช้คนนั้น (แม้สวิตช์ยังปิด) · อ่านอย่างเดียว · บันทึก audit · ออกได้จากแถบสีส้มด้านบน หรือ logout
      </Typography>
      {me?.viewAs && (
        <Alert severity="warning" sx={{ mb: 1, py: 0 }}>
          ตอนนี้กำลังดูในฐานะ {me.viewAs.user_name} ({me.viewAs.user_code}) · {personaText(me.personas)}
        </Alert>
      )}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Autocomplete
          size="small" sx={{ width: 360, maxWidth: '100%' }}
          options={users} value={user} onChange={(_, v) => setUser(v)}
          getOptionLabel={u => `${u.user_name} (${u.user_code})`}
          isOptionEqualToValue={(a, b) => a.user_code === b.user_code}
          renderInput={p => <TextField {...p} label="เลือกผู้ใช้" />}
        />
        <Button variant="contained" disabled={!user || busy} onClick={go}>ดูหน้าเว็บในฐานะผู้ใช้นี้</Button>
        {me?.viewAs && <Button color="warning" onClick={() => stopViewAs(qc)}>ออกจากโหมด</Button>}
      </Box>
    </Paper>
  );
}

export default function InsightConsole() {
  const [tab, setTab] = useState(0);
  const { data: status, refetch, isLoading, error } = useQuery({
    queryKey: ['ic-status'],
    queryFn: async () => (await api.get('/api/insight-console/status')).data,
  });

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>Insight Console</Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error.response?.status === 503 ? 'ระบบ insight ยังไม่ได้ตั้งค่า (ดู backend/.env: INSIGHT_ENABLED, DB_INSIGHT_USER)' : (error.response?.data?.error || error.message)}
        </Alert>
      )}
      {status && <ViewAsPicker />}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" sx={{ mb: 2 }}>
        <Tab label="สถานะ & สวิตช์" />
        <Tab label="นิยามตัวเลข & แผนก" />
        <Tab label="เป้าขาย" />
        <Tab label="ปฏิทิน" />
        <Tab label="ดู KPI แทนผู้ใช้" />
        <Tab label="insight รายวัน" />
      </Tabs>
      {isLoading && <CircularProgress size={24} />}
      {status && (
        <>
          {tab === 0 && <StatusTab status={status} refresh={refetch} />}
          {tab === 1 && <DefinitionTab />}
          {tab === 2 && <TargetsTab />}
          {tab === 3 && <CalendarTab />}
          {tab === 4 && <PreviewTab />}
          {tab === 5 && <DailyTab status={status} refresh={refetch} />}
        </>
      )}
    </Box>
  );
}
